/**
 * Sync Queue Service
 * Manages pending operations and synchronization with the server
 *
 * Features:
 * - Queue operations for later sync
 * - Process queue when online
 * - Handle temp ID to server ID mapping
 * - Retry failed operations with exponential backoff
 */

import { v4 as uuidv4 } from 'uuid';
import { offlineDb, PendingOperation, CachedEntity } from './db';
import { OFFLINE_CONFIG, EntityType, OperationType } from './constants';

// ============== TYPES ==============

export interface QueueOperationParams {
  operationType: OperationType;
  entityType: EntityType;
  data: any;
  tempId?: string;
  serverId?: number;
  gaushalaId: number;
}

export interface SyncResult {
  success: boolean;
  serverId?: number;
  error?: string;
}

// ============== TEMP ID GENERATION ==============

/**
 * Generate a temporary ID for offline-created entities
 */
export function generateTempId(): string {
  return `temp_${uuidv4()}`;
}

/**
 * Check if an ID is a temporary ID
 */
export function isTempId(id: string | number): boolean {
  return typeof id === 'string' && id.startsWith('temp_');
}

// ============== QUEUE OPERATIONS ==============

/**
 * Add an operation to the sync queue
 */
export async function queueOperation(params: QueueOperationParams): Promise<string> {
  const { operationType, entityType, data, tempId, serverId, gaushalaId } = params;

  const operation: PendingOperation = {
    id: uuidv4(),
    operationType,
    entityType,
    data,
    tempId,
    serverId,
    gaushalaId,
    createdAt: new Date(),
    retryCount: 0,
    status: 'pending',
  };

  await offlineDb.pendingOperations.add(operation);
  console.log(`[SyncQueue] Operation queued: ${operationType} ${entityType}`, { id: operation.id });

  return operation.id;
}

/**
 * Get all pending operations for a gaushala
 */
export async function getPendingOperations(gaushalaId?: number): Promise<PendingOperation[]> {
  let query = offlineDb.pendingOperations.where('status').equals('pending');

  const operations = await query.toArray();

  if (gaushalaId !== undefined) {
    return operations.filter(op => op.gaushalaId === gaushalaId);
  }

  return operations;
}

/**
 * Get failed operations
 */
export async function getFailedOperations(): Promise<PendingOperation[]> {
  return offlineDb.pendingOperations.where('status').equals('failed').toArray();
}

/**
 * Retry a failed operation
 */
export async function retryOperation(operationId: string): Promise<void> {
  await offlineDb.pendingOperations.update(operationId, {
    status: 'pending',
    retryCount: 0,
    errorMessage: undefined,
    lastAttempt: undefined,
  });
  console.log(`[SyncQueue] Operation marked for retry: ${operationId}`);
}

/**
 * Remove a completed or cancelled operation
 */
export async function removeOperation(operationId: string): Promise<void> {
  await offlineDb.pendingOperations.delete(operationId);
  console.log(`[SyncQueue] Operation removed: ${operationId}`);
}

// ============== SYNC PROCESSING ==============

/**
 * Process a single sync operation
 * Returns true if successful, false otherwise
 */
export async function processSyncOperation(operation: PendingOperation): Promise<boolean> {
  console.log(`[SyncQueue] Processing operation: ${operation.operationType} ${operation.entityType}`, {
    id: operation.id,
    tempId: operation.tempId,
    serverId: operation.serverId,
  });

  // Mark as syncing
  await offlineDb.pendingOperations.update(operation.id, {
    status: 'syncing',
    lastAttempt: new Date(),
  });

  try {
    // Get the appropriate API service
    const result = await executeOperation(operation);

    if (result.success) {
      // Success! Update cache with server ID if it was a CREATE
      if (operation.operationType === 'CREATE' && operation.tempId && result.serverId) {
        await updateTempIdToServerId(
          operation.entityType,
          operation.tempId,
          result.serverId,
          operation.gaushalaId
        );
      }

      // Mark operation as completed
      await offlineDb.pendingOperations.update(operation.id, {
        status: 'completed',
      });

      console.log(`[SyncQueue] Operation completed: ${operation.id}`);
      return true;
    } else {
      // Failed - increment retry count
      const newRetryCount = operation.retryCount + 1;
      const shouldMarkFailed = newRetryCount >= OFFLINE_CONFIG.MAX_RETRY_COUNT;

      await offlineDb.pendingOperations.update(operation.id, {
        status: shouldMarkFailed ? 'failed' : 'pending',
        retryCount: newRetryCount,
        errorMessage: result.error,
      });

      console.log(`[SyncQueue] Operation ${shouldMarkFailed ? 'failed' : 'will retry'}: ${operation.id}`, {
        error: result.error,
        retryCount: newRetryCount,
      });

      return false;
    }
  } catch (error) {
    // Exception occurred
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    const newRetryCount = operation.retryCount + 1;
    const shouldMarkFailed = newRetryCount >= OFFLINE_CONFIG.MAX_RETRY_COUNT;

    await offlineDb.pendingOperations.update(operation.id, {
      status: shouldMarkFailed ? 'failed' : 'pending',
      retryCount: newRetryCount,
      errorMessage,
    });

    console.error(`[SyncQueue] Operation exception: ${operation.id}`, error);
    return false;
  }
}

/**
 * Execute the actual API call for an operation
 */
async function executeOperation(operation: PendingOperation): Promise<SyncResult> {
  const { operationType, entityType, data, serverId } = operation;

  // Dynamically import API services to avoid circular dependencies
  const { gauShalaApi } = await import('../../services/gaushala/api');

  try {
    switch (entityType) {
      case 'cattle':
        return await executeCattleOperation(operationType, data, serverId, gauShalaApi);

      case 'healthRecord':
        return await executeHealthRecordOperation(operationType, data, serverId, gauShalaApi);

      case 'milkProduction':
        return await executeMilkProductionOperation(operationType, data, serverId, gauShalaApi);

      case 'foodHistory':
        return await executeFoodHistoryOperation(operationType, data, serverId, gauShalaApi);

      case 'medicine':
        return await executeMedicineOperation(operationType, data, serverId, gauShalaApi);

      case 'inventory':
        return await executeInventoryOperation(operationType, data, serverId, gauShalaApi);

      case 'shed':
        return await executeShedOperation(operationType, data, serverId, gauShalaApi);

      case 'dungTransaction':
        return await executeDungTransactionOperation(operationType, data, serverId, gauShalaApi);

      default:
        return { success: false, error: `Unsupported entity type: ${entityType}` };
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Operation failed',
    };
  }
}

// ============== ENTITY-SPECIFIC OPERATIONS ==============

async function executeCattleOperation(
  operationType: OperationType,
  data: any,
  serverId: number | undefined,
  api: any
): Promise<SyncResult> {
  switch (operationType) {
    case 'CREATE': {
      const response = await api.cattle.createCattle(data);
      if (response.success && response.data?.id) {
        return { success: true, serverId: response.data.id };
      }
      return { success: false, error: response.error || 'Failed to create cattle' };
    }
    case 'UPDATE': {
      if (!serverId) return { success: false, error: 'Missing server ID for update' };
      const response = await api.cattle.updateCattle(serverId, data);
      return { success: response.success, error: response.error };
    }
    case 'DELETE': {
      if (!serverId) return { success: false, error: 'Missing server ID for delete' };
      const response = await api.cattle.deleteCattle(serverId);
      return { success: response.success, error: response.error };
    }
    default:
      return { success: false, error: `Unsupported operation: ${operationType}` };
  }
}

async function executeHealthRecordOperation(
  operationType: OperationType,
  data: any,
  serverId: number | undefined,
  api: any
): Promise<SyncResult> {
  switch (operationType) {
    case 'CREATE': {
      const response = await api.healthRecords.createHealthRecord(data);
      if (response.success && response.data?.id) {
        return { success: true, serverId: response.data.id };
      }
      return { success: false, error: response.error || 'Failed to create health record' };
    }
    case 'UPDATE': {
      if (!serverId) return { success: false, error: 'Missing server ID for update' };
      const response = await api.healthRecords.updateHealthRecord(serverId, data);
      return { success: response.success, error: response.error };
    }
    case 'DELETE': {
      if (!serverId) return { success: false, error: 'Missing server ID for delete' };
      const response = await api.healthRecords.deleteHealthRecord(serverId);
      return { success: response.success, error: response.error };
    }
    default:
      return { success: false, error: `Unsupported operation: ${operationType}` };
  }
}

async function executeMilkProductionOperation(
  operationType: OperationType,
  data: any,
  serverId: number | undefined,
  api: any
): Promise<SyncResult> {
  switch (operationType) {
    case 'CREATE': {
      const response = await api.milkProduction.createMilkRecord(data);
      if (response.success && response.data?.id) {
        return { success: true, serverId: response.data.id };
      }
      return { success: false, error: response.error || 'Failed to create milk record' };
    }
    case 'UPDATE': {
      if (!serverId) return { success: false, error: 'Missing server ID for update' };
      const response = await api.milkProduction.updateMilkRecord(serverId, data);
      return { success: response.success, error: response.error };
    }
    case 'DELETE': {
      if (!serverId) return { success: false, error: 'Missing server ID for delete' };
      const response = await api.milkProduction.deleteMilkRecord(serverId);
      return { success: response.success, error: response.error };
    }
    default:
      return { success: false, error: `Unsupported operation: ${operationType}` };
  }
}

async function executeFoodHistoryOperation(
  operationType: OperationType,
  data: any,
  serverId: number | undefined,
  api: any
): Promise<SyncResult> {
  switch (operationType) {
    case 'CREATE': {
      const response = await api.foodHistory.createFoodHistory(data);
      if (response.success && response.data?.id) {
        return { success: true, serverId: response.data.id };
      }
      return { success: false, error: response.error || 'Failed to create food history' };
    }
    case 'UPDATE': {
      if (!serverId) return { success: false, error: 'Missing server ID for update' };
      const response = await api.foodHistory.updateFoodHistory(serverId, data);
      return { success: response.success, error: response.error };
    }
    case 'DELETE': {
      if (!serverId) return { success: false, error: 'Missing server ID for delete' };
      const response = await api.foodHistory.deleteFoodHistory(serverId);
      return { success: response.success, error: response.error };
    }
    default:
      return { success: false, error: `Unsupported operation: ${operationType}` };
  }
}

async function executeMedicineOperation(
  operationType: OperationType,
  data: any,
  serverId: number | undefined,
  api: any
): Promise<SyncResult> {
  switch (operationType) {
    case 'CREATE': {
      const response = await api.medicine.createMedicine(data);
      if (response.success && response.data?.id) {
        return { success: true, serverId: response.data.id };
      }
      return { success: false, error: response.error || 'Failed to create medicine' };
    }
    case 'UPDATE': {
      if (!serverId) return { success: false, error: 'Missing server ID for update' };
      const response = await api.medicine.updateMedicine(serverId, data);
      return { success: response.success, error: response.error };
    }
    case 'DELETE': {
      if (!serverId) return { success: false, error: 'Missing server ID for delete' };
      const response = await api.medicine.deleteMedicine(serverId);
      return { success: response.success, error: response.error };
    }
    default:
      return { success: false, error: `Unsupported operation: ${operationType}` };
  }
}

async function executeInventoryOperation(
  operationType: OperationType,
  data: any,
  serverId: number | undefined,
  api: any
): Promise<SyncResult> {
  switch (operationType) {
    case 'CREATE': {
      const response = await api.inventory.createInventory(data);
      if (response.success && response.data?.id) {
        return { success: true, serverId: response.data.id };
      }
      return { success: false, error: response.error || 'Failed to create inventory' };
    }
    case 'UPDATE': {
      if (!serverId) return { success: false, error: 'Missing server ID for update' };
      const response = await api.inventory.updateInventory(serverId, data);
      return { success: response.success, error: response.error };
    }
    case 'DELETE': {
      if (!serverId) return { success: false, error: 'Missing server ID for delete' };
      const response = await api.inventory.deleteInventory(serverId);
      return { success: response.success, error: response.error };
    }
    default:
      return { success: false, error: `Unsupported operation: ${operationType}` };
  }
}

async function executeShedOperation(
  operationType: OperationType,
  data: any,
  serverId: number | undefined,
  api: any
): Promise<SyncResult> {
  switch (operationType) {
    case 'CREATE': {
      const response = await api.sheds.createShed(data);
      if (response.success && response.data?.id) {
        return { success: true, serverId: response.data.id };
      }
      return { success: false, error: response.error || 'Failed to create shed' };
    }
    case 'UPDATE': {
      if (!serverId) return { success: false, error: 'Missing server ID for update' };
      const response = await api.sheds.updateShed(serverId, data);
      return { success: response.success, error: response.error };
    }
    case 'DELETE': {
      if (!serverId) return { success: false, error: 'Missing server ID for delete' };
      const response = await api.sheds.deleteShed(serverId);
      return { success: response.success, error: response.error };
    }
    default:
      return { success: false, error: `Unsupported operation: ${operationType}` };
  }
}

async function executeDungTransactionOperation(
  operationType: OperationType,
  data: any,
  serverId: number | undefined,
  api: any
): Promise<SyncResult> {
  switch (operationType) {
    case 'CREATE': {
      const response = await api.dungRecords.createDungRecord(data);
      if (response.success && response.data?.id) {
        return { success: true, serverId: response.data.id };
      }
      return { success: false, error: response.error || 'Failed to create dung record' };
    }
    default:
      return { success: false, error: `Unsupported operation for dung: ${operationType}` };
  }
}

// ============== TEMP ID MAPPING ==============

/**
 * Update cached entity from temp ID to server ID after successful sync
 */
async function updateTempIdToServerId(
  entityType: EntityType,
  tempId: string,
  serverId: number,
  gaushalaId: number
): Promise<void> {
  const table = offlineDb.getCacheTable(entityType);

  // Get the cached entity with temp ID
  const cached = await table.get(tempId);

  if (cached) {
    // Delete the temp entry
    await table.delete(tempId);

    // Add with server ID
    await table.put({
      ...cached,
      id: serverId,
      isTemp: false,
      syncStatus: 'synced',
      data: {
        ...cached.data,
        id: serverId,
      },
    });

    console.log(`[SyncQueue] Updated tempId ${tempId} to serverId ${serverId} for ${entityType}`);
  }
}

// ============== EXPORTS ==============

export const syncQueue = {
  add: queueOperation,
  getPending: getPendingOperations,
  getFailed: getFailedOperations,
  retry: retryOperation,
  remove: removeOperation,
  process: processSyncOperation,
};
